// @file utils for surface polls

import { buildUrlFromPath } from '@@/bits/location'
import type { DraftPost, Poll, Post, PostAttributes } from '@@/types'
import { captureException } from '@sentry/vue'

export const isPollClosed = (poll: Poll): boolean => {
  return poll.closed_at !== null
}

export const hasPollAttachment = (post: Post | DraftPost | PostAttributes): boolean => {
  return post.wish_content?.attachment_props?.type === 'poll'
}

export const getPollFromPost = (post: Post | DraftPost | PostAttributes): Poll | null => {
  if (post.wish_content?.attachment_props != null && hasPollAttachment(post)) {
    return post.wish_content?.attachment_props as Poll
  }
  return null
}

export const buildCsvExportsUrl = (publicKey: string, post: Post): string | null => {
  const poll = getPollFromPost(post)
  if (poll != null && post.id != null) {
    return buildUrlFromPath(`padlets/${publicKey}/wishes/${post.id}/polls/${poll.poll_id}/exports/csv`)
  }
  return null
}

export const exportPollResultsAsCSV = async (publicKey: string, post: Post): Promise<void> => {
  const csvExportsUrl = buildCsvExportsUrl(publicKey, post)
  if (csvExportsUrl != null) {
    window.open(csvExportsUrl)
  } else {
    captureException('Poll not found when exporting poll results as CSV')
  }
}

/**
 * Calculate poll percentages using the Largest Remainder Method (Hamilton's method)
 * Constraints:
 * 1. Choices with equal vote counts must display the same percentage.
 * 2. If we cannot distribute the remaining units without breaking constraint 1,
 *    we either drop the remainder or distribute it equally such that
 *    the sum of the percentages is as close to 100% as possible.
 *     - e.g. vote share of 1, 1, 1 should show as 33.3%, 33.3%, 33.3% (total 99.9%) instead of 33.4%, 33.4%, 33.4% (total 100.2%)
 *     - e.g. vote share of 1, 1, 1, 1, 1, 1, 1 should show as 14.3% each (total 100.1%) instead of 14.2% each (total 99.4%)
 *
 * @param voteCounts - Array of vote counts for each choice
 * @param decimalPlaces - Number of decimal places for precision (default: 1)
 * @returns Array of percentages where equal vote counts have equal percentages
 */
export const calculatePollPercentages = (voteCounts: number[], decimalPlaces: number = 1): number[] => {
  const totalVotes = voteCounts.reduce((sum, count) => sum + count, 0)

  if (totalVotes === 0) {
    return Array(voteCounts.length).fill(0)
  }

  // Work in integer units to avoid floating point errors.
  // e.g., for 1 decimal place, multiply by 10 so 33.3% becomes 333 units.
  const multiplier = Math.pow(10, decimalPlaces)
  const target = 100 * multiplier

  // Group choices by vote count so equal votes get equal percentages.
  // e.g., [5, 1, 6, 1] creates groups: {1: size 2, 5: size 1, 6: size 1}
  // Track unitsToDistribute by deducting floorPercentage as we go.
  const voteCountToData = new Map<number, { groupSize: number; floorPercentage: number; remainder: number }>()
  let unitsToDistribute = target

  for (const count of voteCounts) {
    const existing = voteCountToData.get(count)
    if (existing != null) {
      existing.groupSize++
      unitsToDistribute -= existing.floorPercentage
    } else {
      const rawPercentage = (count / totalVotes) * target
      // Floor to ensure we never exceed 100% before distributing remainder
      const floorPercentage = Math.floor(rawPercentage)
      voteCountToData.set(count, {
        groupSize: 1,
        floorPercentage,
        // Track fractional part lost by flooring - used to decide who gets remainder
        remainder: rawPercentage - floorPercentage,
      })
      unitsToDistribute -= floorPercentage
    }
  }

  // Largest Remainder Method: give +1 to groups with highest remainders first.
  // This minimizes total rounding error.
  const sortedEntries = [...voteCountToData.entries()].sort((a, b) => b[1].remainder - a[1].remainder)

  // Distribute units to entire groups at once (not individual choices) so
  // all choices with the same vote count get the same percentage.
  for (const [, data] of sortedEntries) {
    if (unitsToDistribute <= 0) break
    if (unitsToDistribute >= data.groupSize) {
      // We have enough units to bump all members of this group
      data.floorPercentage++
      unitsToDistribute -= data.groupSize
    } else {
      // Can't fully bump this group. Check if bumping anyway gets closer to 100%.
      // undershoot = how far below 100% if we don't bump
      // overshoot = how far above 100% if we bump
      const undershoot = unitsToDistribute
      const overshoot = data.groupSize - unitsToDistribute
      if (overshoot < undershoot) {
        data.floorPercentage++
        unitsToDistribute -= data.groupSize
      }
      // Stop after considering the first group we can't fully bump
      // (it has the highest remainder, so bumping others would be less optimal)
      break
    }
  }

  // Convert back from integer units to decimal percentages
  return voteCounts.map((count) => voteCountToData.get(count)!.floorPercentage / multiplier)
}
